// src/App.jsx
import { useRoutes, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useEffect, useState } from 'react';
import { CssBaseline } from '@mui/material';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import ThemeProvider from './theme/ThemeProvider';
import router from './router';
import LoaderScreen from './LoaderScreen';
import ServerDownScreen from './ServerDownScreen';
import { socket } from './socket/config';
import { collabSocket } from './socket/collabSocket';
import { notificationsSocket } from './socket/notificationsSocket';
import { useNotificationAudio } from './utils/useNotificationAudio';
import { getProfile, logout, silentRefresh } from './redux/authSlice';
import { checkSession } from './redux/config';
import { PackFeaturesProvider, usePackFeatures } from './contexts/PackFeaturesContext';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './i18n';

function ProtectedRoute({ children }) {
  const location = useLocation();
  const dispatch = useDispatch();
  const authUser = useSelector((x) => x.user);
  const PUBLIC_ROUTES = ['/', '/404'];
  const isPublicPage = PUBLIC_ROUTES.includes(location.pathname);

  // On cold load with no access token, there may still be a valid httpOnly
  // refresh cookie (e.g. localStorage was cleared but the session wasn't).
  // Try a silent refresh once before deciding the user is logged out, so we
  // don't force a needless re-login.
  const [sessionChecked, setSessionChecked] = useState(false);
  useEffect(() => {
    if (authUser.accessToken || isPublicPage) {
      setSessionChecked(true);
      return;
    }
    dispatch(silentRefresh()).finally(() => setSessionChecked(true));
    // Only ever run this once per mount — re-running on every accessToken
    // change would re-trigger after a successful refresh.
    // eslint-disable-next-line
  }, []);

  // Fetch profile when authenticated (hook must be before any return)
  useEffect(() => {
    if (authUser.accessToken) {
      dispatch(getProfile());
    }
  }, [authUser.accessToken, dispatch]);

  // The backend decides whether the company can use its subscription (3 days
  // to send the transfer proof, 10 days to get it approved, end date). Once
  // its blocking is switched on (blockingEnabled), that answer replaces the
  // rules below, so web and API always agree. Loaded by PackFeaturesProvider,
  // which also gives the menu and the pages the features of the pack.
  const { status: subscriptionAccess } = usePackFeatures();

  if (!sessionChecked) {
    return <LoaderScreen />;
  }

  // If not authenticated and not on a public page → redirect to login
  if (!authUser.accessToken && !isPublicPage) {
    window.location.href = process.env.REACT_APP_ADMIN_URL + '/login';
    return null;
  }

  // Check subscription validity once profile is loaded
  const profile = authUser.profile;
  const subscriptions = profile?.company?.subscriptions;

  if (authUser.accessToken && !isPublicPage && subscriptions) {
    // No subscriptions at all → redirect to guest app
    if (subscriptions.length === 0) {
      window.location.href = process.env.REACT_APP_ADMIN_URL;
      return null;
    }

    // Check the last (current) subscription
    const lastSub = subscriptions[subscriptions.length - 1];
    const hasActiveStatus =
      lastSub?.status === 'ACTIVE' || lastSub?.status === 'TRIAL';

    // Expired (or any non-active) status → block immediately
    if (!hasActiveStatus) {
      window.location.href = process.env.REACT_APP_ADMIN_URL;
      return null;
    }

    if (subscriptionAccess?.blockingEnabled) {
      // A rejected proof can be sent again until day 10: only the backend's
      // verdict counts.
      if (subscriptionAccess.usable === false) {
        window.location.href = process.env.REACT_APP_ADMIN_URL;
        return null;
      }
    } else if (lastSub?.verificationStatus === 'REJECTED') {
      // Active/Trial status → block only if verification is REJECTED
      window.location.href = process.env.REACT_APP_ADMIN_URL;
      return null;
    }
  }

  // Issue 2: If the user is locked, block access
  if (authUser.accessToken && profile) {
    if (profile.locked === true || profile.locked === 'true') {
      // Force them back to login (or logout)
      dispatch(logout());
      return null;
    }

    // Issue 3: If user is specific role and hasn't changed password, force navigation to settings
    const FORCED_PASSWORD_ROLES = ['MANAGER', 'CLAIM_MANAGER', 'RESPONSIBLE'];
    if (
      profile.password_changed === false && 
      FORCED_PASSWORD_ROLES.includes(authUser.role) && 
      !isPublicPage
    ) {
      if (location.pathname !== '/management/profile/settings') {
        window.location.href = '/management/profile/settings';
        return null;
      }
    }
  }

  return children;
}

function App() {
  const content = useRoutes(router);
  const authUser = useSelector((x) => x.user);
  const serverDown = useSelector((x) => x.ui.serverDown);

  // ✅ Initialize audio unlock globally
  const { audioReady } = useNotificationAudio();

  // ✅ Log audio status
  useEffect(() => {
    if (audioReady) {
      console.log('🔊 Audio system ready');
    }
  }, [audioReady]);

  // ✅ Initialize sockets
  useEffect(() => {
    if (authUser.accessToken) {
      socket.connect();
      socket.on('connect', () => console.log('✅ Socket connected'));
      socket.on('disconnect', (reason) =>
        console.log('⚠️ Socket disconnected:', reason)
      );
      socket.on('connect_error', (err) =>
        console.error('❌ Socket error:', err)
      );

      const onCollabConnect = () => console.log('✅ CollabSocket connected');
      const onCollabDisconnect = (reason) => console.log('⚠️ CollabSocket disconnected:', reason);
      const onCollabError = (err) => console.error('❌ CollabSocket error:', err);

      collabSocket.connect();
      collabSocket.on('connect', onCollabConnect);
      collabSocket.on('disconnect', onCollabDisconnect);
      collabSocket.on('connect_error', onCollabError);

      const onNotifConnect = () => console.log('✅ NotificationsSocket connected');
      const onNotifDisconnect = (reason) => console.log('⚠️ NotificationsSocket disconnected:', reason);
      const onNotifError = (err) => console.error('❌ NotificationsSocket error:', err);

      // The backend ended this session (logout on guest, password changed…):
      // check it now rather than at the next click. Still alive (another
      // login in this browser, same account): reconnect, the server closed
      // this socket.
      const onSessionEnded = async () => {
        if (await checkSession()) notificationsSocket.connect();
      };

      // An idle tab coming back may be on a session that ended meanwhile.
      let lastCheck = Date.now();
      const onVisible = () => {
        if (
          document.visibilityState !== 'visible' ||
          Date.now() - lastCheck < 10_000
        )
          return;
        lastCheck = Date.now();
        checkSession();
      };

      notificationsSocket.connect();
      notificationsSocket.on('connect', onNotifConnect);
      notificationsSocket.on('disconnect', onNotifDisconnect);
      notificationsSocket.on('connect_error', onNotifError);
      notificationsSocket.on('session_ended', onSessionEnded);
      document.addEventListener('visibilitychange', onVisible);

      return () => {
        socket.off('connect');
        socket.off('disconnect');
        socket.off('connect_error');
        socket.disconnect();
        collabSocket.off('connect', onCollabConnect);
        collabSocket.off('disconnect', onCollabDisconnect);
        collabSocket.off('connect_error', onCollabError);
        collabSocket.disconnect();
        notificationsSocket.off('connect', onNotifConnect);
        notificationsSocket.off('disconnect', onNotifDisconnect);
        notificationsSocket.off('connect_error', onNotifError);
        notificationsSocket.off('session_ended', onSessionEnded);
        document.removeEventListener('visibilitychange', onVisible);
        notificationsSocket.disconnect();
      };
    }
  }, [authUser.accessToken]);

  return (
    <ThemeProvider>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <CssBaseline />
        <ToastContainer
          position="bottom-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="dark"
        />
        {serverDown ? (
          <ServerDownScreen />
        ) : (
          <PackFeaturesProvider>
            <ProtectedRoute>
              {content}
            </ProtectedRoute>
          </PackFeaturesProvider>
        )}
      </LocalizationProvider>
    </ThemeProvider>
  );
}

export default App;
